import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Paths } from "src/navigation";
import {
  GET_TOKEN_HEADER,
  useGetCommunicationToken,
  useGetVehicleDraft,
} from "src/networking";
import {
  usePostCreateDossier,
  usePostSendDossier,
} from "src/networking/terminals/signature/signature.gates";
import {
  useDraftCAIStore,
  useDraftIDStore,
} from "src/storage/formik/formik-draft.store";
import {
  AuthType,
  BozzaStepDTO,
  CreateDossierRequestBodyDTO,
  FormikSignatureB,
  SendDossierRequestBodyDTO,
  SignatureType,
  VehicleMapper,
  VehicleType,
} from "src/types";
import { useBoolean } from "src/utils";
import { useSignatureValidationSchema } from "./validation-schemas/signature-validation-schema.hook";

export const useSignatureB = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlIdBozza = searchParams.get("code");

  const [signatureChoiceVisibility, showSignatureChoice, _] = useBoolean(false);
  const [isFromCommunicationToken, setIsFromCommunicationToken] =
    useState(false);
  const [isLoadingCommunicationToken, setIsLoadingCommunicationToken] =
    useState(!!urlIdBozza);

  const { crossGate: postSendDossier } = usePostSendDossier();
  const { crossGate: postCreateDossier } = usePostCreateDossier();
  const { crossGate: getVehicleDraft } = useGetVehicleDraft();
  const { crossGate: getCommunicationToken } = useGetCommunicationToken();

  const draftId = useDraftIDStore((state) => state.draftID);
  const setDraftID = useDraftIDStore((state) => state.setDraftID);

  const formikCAI = useDraftCAIStore((state) => state.draft);
  const updateVehicleDraft = useDraftCAIStore(
    (state) => state.updateVehicleDraft,
  );

  const { schema: signatureValidationSchema } = useSignatureValidationSchema();
  const formikSignatureB = useFormik<FormikSignatureB>({
    initialValues: {
      email: "",
      phoneNumber: {
        formattedPhone: "",
      },
    },
    validationSchema: signatureValidationSchema,
    onSubmit: (_, { setTouched, setSubmitting }) => {
      setTouched({});
      setSubmitting(false);
      createDossier(null);
    },
  });

  const fetchVehicleDraft = async (idToFetch: string) => {
    if (!idToFetch) return;

    let stepDto = BozzaStepDTO.VEICOLO_A;
    let vehicleType = VehicleType.VEHICLE_A;

    await getVehicleDraft(
      {
        headers: { step: stepDto },
        uriParams: { idBozza: idToFetch },
      },
      {
        onSuccess: (response) => {
          const vehicleData = VehicleMapper.fromDTO(response);
          updateVehicleDraft({ vehicleType, vehicleDraft: vehicleData });
        },
        onFailure: (error) => {
          console.error("Error fetching vehicle draft:", error);
        },
      },
    );

    stepDto = BozzaStepDTO.VEICOLO_B;
    vehicleType = VehicleType.VEHICLE_B;

    await getVehicleDraft(
      {
        headers: { step: stepDto },
        uriParams: { idBozza: idToFetch },
      },
      {
        onSuccess: (response) => {
          const vehicleData = VehicleMapper.fromDTO(response);
          updateVehicleDraft({ vehicleType, vehicleDraft: vehicleData });
        },
        onFailure: (error) => {
          console.error("Error fetching vehicle draft:", error);
        },
      },
    );
  };

  const handleCommunicationTokenFlow = async (code: string) => {
    if (!code) return;

    await getCommunicationToken(
      {
        headers: GET_TOKEN_HEADER,
        uriParams: { code },
      },
      {
        onSuccess: (response) => {
          const { idBozza, email, telefono } = response.data;

          setDraftID(idBozza);

          setIsFromCommunicationToken(true);

          formikSignatureB.setFieldValue("email", email || "");
          formikSignatureB.setFieldValue(
            "phoneNumber.formattedPhone",
            telefono || "",
          );

          showSignatureChoice();

          fetchVehicleDraft(idBozza);

          setIsLoadingCommunicationToken(false);
        },
        onFailure: (error) => {
          console.error("Error fetching communication token:", error);
          setIsLoadingCommunicationToken(false);
        },
      },
    );
  };

  useEffect(() => {
    if (urlIdBozza) {
      handleCommunicationTokenFlow(urlIdBozza);
    } else if (draftId) {
      fetchVehicleDraft(draftId);
    }
  }, [urlIdBozza, draftId]);

  const { values } = formikSignatureB;

  const sendDossier = () => {
    if (!draftId) return;

    const body: SendDossierRequestBodyDTO = {
      idRichiesta: draftId,
    };

    postSendDossier(
      { body },
      {
        onSuccess: (_) => {
          navigate(Paths.SignatureResult + "?status=OK", { replace: true });
        },
        onFailure: (error) => {
          console.error("Error sending dossier:", error);
          navigate(Paths.SignatureResult + "?status=KO", { replace: true });
        },
      },
    );
  };

  const createDossier = (authType: AuthType | null) => {
    const phone = values.phoneNumber;
    const email = values.email;

    if (!draftId || (!phone && !email)) return;

    const body: CreateDossierRequestBodyDTO = {
      idRichiesta: draftId,
      firmatario: {
        tipo: SignatureType.DIRVER_B,
        tipoAutenticazione: authType,
        telefono: phone.formattedPhone,
        email: email,
      },
    };

    postCreateDossier(
      { body },
      {
        onSuccess: (response) => {
          if (isFromCommunicationToken) {
            window.location.replace(response.dossier);
          } else {
            const emailParam = encodeURIComponent(email);
            navigate(`${Paths.SignatureResult}?status=OK&email=${emailParam}`, {
              replace: true,
            });
          }
        },
        onFailure: (error) => {
          console.error("Error creating dossier:", error);
          navigate(Paths.SignatureResult + "?status=KO", { replace: true });
        },
      },
    );
  };

  return {
    formikCAI,
    formikSignatureB,
    signatureValidationSchema,
    signatureChoiceVisibility,
    isLoadingCommunicationToken,
    t,
    sendDossier,
    createDossier,
    showSignatureChoice,
  };
};
